import React from "react";
import * as Yup from "yup";
import { Formik } from "formik";
import { Grid, Modal, Typography, TextField, FormControlLabel, Checkbox, Autocomplete, Chip, Stack } from "@mui/material";

import { Button, Alert } from "../../../common/styled";
import ModalBox from "../../../../common/ModalBox";

import useLLMConfig from "../../../../../hooks/user/useLLMConfig";

function AnonymizeModal({ open, handleClose, zoneUid }) {
  const { llmPromptScanners, configLLMSettingPromptScanners } = useLLMConfig();

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        preamble: llmPromptScanners ? llmPromptScanners?.anonymize?.preamble : "",
        hidden_names: llmPromptScanners ? llmPromptScanners?.anonymize?.hidden_names : [],
        allowed_names: llmPromptScanners ? llmPromptScanners?.anonymize?.allowed_names : [],
        entity_types: llmPromptScanners ? llmPromptScanners?.anonymize?.entity_types : [],
        use_faker: llmPromptScanners ? llmPromptScanners?.anonymize?.use_faker : false,
        threshold: llmPromptScanners ? llmPromptScanners?.anonymize?.threshold : 0,
      }}
      validationSchema={Yup.object().shape({
        preamble: Yup.string().nullable(),
        hidden_names: Yup.array().of(Yup.string()),
        allowed_names: Yup.array().of(Yup.string()),
        entity_types: Yup.array().of(Yup.string()),
        use_faker: Yup.bool(),
        threshold: Yup.number().min(0, "Threshold must be at least 0").max(1, "Threshold must be at most 1"),
      })}
      onSubmit={async (values, { resetForm, setErrors, setStatus, setSubmitting }) => {
        try {
          values.submit = undefined;
          await configLLMSettingPromptScanners(zoneUid, "anonymize", values);
          handleClose();
          resetForm();
        } catch (error) {
          const message = error.message || "Something went wrong";
          setStatus({ success: false });
          setErrors({ submit: message });
          setSubmitting(false);
        }
      }}
    >
      {({ errors, handleBlur, handleChange, handleSubmit, setFieldValue, isSubmitting, touched, values }) => (
        <React.Fragment>
          <Modal
            open={open}
            onClose={(_, reason) => {
              if (reason !== "backdropClick") {
                handleClose();
              }
            }}
          >
            <ModalBox sx={{ width: "80vw" }} title="Edit Anonymize Scanner" handleClose={handleClose}>
              <form noValidate onSubmit={handleSubmit}>
                <Grid container p={4} spacing={3}>
                  <Grid item xs={12}>
                    {errors.submit && (
                      <Alert mt={2} mb={3} variant="outlined" severity="error">
                        {errors.submit}
                      </Alert>
                    )}
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Stack direction="column" spacing={2} alignItems="flex-start">
                      <Typography>Preamble</Typography>
                      <TextField
                        fullWidth
                        name="preamble"
                        value={values.preamble}
                        error={Boolean(touched.preamble && errors.preamble)}
                        helperText={touched.preamble && errors.preamble}
                        onBlur={handleBlur}
                        onChange={handleChange}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Stack direction="column" spacing={2} alignItems="flex-start">
                      <Typography>Threshold</Typography>
                      <TextField
                        fullWidth
                        name="threshold"
                        type="number"
                        error={Boolean(touched.threshold && errors.threshold)}
                        helperText={touched.threshold && errors.threshold}
                        value={values.threshold}
                        onBlur={(e) => {
                          if (e.target.value === "") setFieldValue("threshold", 0);
                        }}
                        onChange={handleChange}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={4} mt={4}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          name="use_faker"
                          checked={values.use_faker}
                          error={Boolean(touched.use_faker && errors.use_faker)}
                          helperText={touched.use_faker && errors.use_faker}
                          onBlur={handleBlur}
                          onChange={handleChange}
                          color="primary"
                        />
                      }
                      label="Use Faker"
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Stack direction="column" spacing={2} alignItems="flex-start">
                      <Typography>Anonymize Entities</Typography>
                      <Autocomplete
                        fullWidth
                        multiple
                        freeSolo
                        name="entity_types"
                        options={[]}
                        value={values.entity_types}
                        error={Boolean(touched.entity_types && errors.entity_types)}
                        helperText={touched.entity_types && errors.entity_types}
                        onChange={(event, newValue) => setFieldValue("entity_types", newValue)}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => <Chip variant="filled" color="warning" label={option} {...getTagProps({ index })} />)
                        }
                        renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Type and press Enter" />}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Stack direction="column" spacing={2} alignItems="flex-start">
                      <Typography>Hidden Names</Typography>
                      <Autocomplete
                        fullWidth
                        multiple
                        freeSolo
                        name="hidden_names"
                        options={[]}
                        value={values.hidden_names}
                        error={Boolean(touched.hidden_names && errors.hidden_names)}
                        helperText={touched.hidden_names && errors.hidden_names}
                        onChange={(event, newValue) => setFieldValue("hidden_names", newValue)}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => <Chip variant="filled" color="warning" label={option} {...getTagProps({ index })} />)
                        }
                        renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Type and press Enter" />}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Stack direction="column" spacing={2} alignItems="flex-start">
                      <Typography>Allowed Names</Typography>
                      <Autocomplete
                        fullWidth
                        multiple
                        freeSolo
                        name="allowed_names"
                        options={[]}
                        value={values.allowed_names}
                        error={Boolean(touched.allowed_names && errors.allowed_names)}
                        helperText={touched.allowed_names && errors.allowed_names}
                        onChange={(event, newValue) => setFieldValue("allowed_names", newValue)}
                        renderTags={(value, getTagProps) =>
                          value.map((option, index) => <Chip variant="filled" color="warning" label={option} {...getTagProps({ index })} />)
                        }
                        renderInput={(params) => <TextField {...params} variant="outlined" placeholder="Type and press Enter" />}
                      />
                    </Stack>
                  </Grid>
                  <Grid item xs={12} textAlign={"right"} pt={4}>
                    <Stack direction="row" spacing={2} justifyContent="flex-end" mt={4}>
                      <Button variant="outlined" color="secondary" size="modal" onClick={handleClose}>
                        Cancel
                      </Button>
                      <Button type="submit" variant="contained" color="success" size="modal" disabled={isSubmitting}>
                        Save
                      </Button>
                    </Stack>
                  </Grid>
                </Grid>
              </form>
            </ModalBox>
          </Modal>
        </React.Fragment>
      )}
    </Formik>
  );
}

export default AnonymizeModal;
